/**
 * Social Share Component
 * Allows users to share content on various social media platforms
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Share2, 
  Facebook, 
  Twitter, 
  Linkedin, 
  MessageCircle, 
  Copy, 
  Check,
  Send
} from 'lucide-react';
import { Button } from './ui/button';

// WhatsApp icon (not in lucide-react)
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Telegram icon
const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

export default function SocialShare({ 
  url, 
  title, 
  description, 
  image,
  variant = 'default', // 'default', 'compact', 'icon-only'
  className = ''
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Encode for URL parameters
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description || '');
  
  // Share URLs for different platforms
  const shareUrls = {
    whatsapp: `https://wa.me/?text=${encodedTitle}%0A%0A${encodedDescription}%0A%0A${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`
  };

  const handleShare = (platform) => {
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: url,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      setShowAll(!showAll);
    }
  };

  // Platform buttons configuration
  const platforms = [
    { key: 'whatsapp', icon: WhatsAppIcon, label: 'WhatsApp', color: 'hover:bg-green-100 hover:text-green-600' },
    { key: 'facebook', icon: Facebook, label: 'Facebook', color: 'hover:bg-blue-100 hover:text-blue-600' },
    { key: 'twitter', icon: Twitter, label: 'X', color: 'hover:bg-gray-100 hover:text-gray-800' },
    { key: 'telegram', icon: TelegramIcon, label: 'Telegram', color: 'hover:bg-sky-100 hover:text-sky-600' },
    { key: 'linkedin', icon: Linkedin, label: 'LinkedIn', color: 'hover:bg-blue-100 hover:text-blue-700' },
  ];

  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Button
          variant="outline"
          size="sm"
          onClick={handleNativeShare}
          className="gap-2"
          data-testid="share-button"
        >
          <Share2 className="h-4 w-4" />
          {t('share.share')}
        </Button>
        
        {showAll && (
          <div className="flex items-center gap-1 animate-fade-in">
            {platforms.map(({ key, icon: Icon, color }) => (
              <button
                key={key}
                onClick={() => handleShare(key)}
                className={`p-2 rounded-full transition-colors ${color}`}
                title={key}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
            <button
              onClick={handleCopyLink}
              className="p-2 rounded-full hover:bg-amber-100 hover:text-amber-600 transition-colors"
              title={t('share.copyLink')}
            >
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Default variant - full share section
  return (
    <div className={`${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <Share2 className="h-5 w-5 text-secondary" />
        <span className="font-medium text-primary">{t('share.shareThis')}</span>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {/* WhatsApp - most important for Spain */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleShare('whatsapp')}
          className="gap-2 hover:bg-green-50 hover:text-green-600 hover:border-green-200"
          data-testid="share-whatsapp"
        >
          <WhatsAppIcon className="h-4 w-4" />
          WhatsApp
        </Button>
        
        {/* Facebook */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleShare('facebook')}
          className="gap-2 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200"
          data-testid="share-facebook"
        >
          <Facebook className="h-4 w-4" />
          Facebook
        </Button>
        
        {/* Twitter/X */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleShare('twitter')}
          className="gap-2 hover:bg-gray-50 hover:text-gray-800 hover:border-gray-300"
          data-testid="share-twitter"
        >
          <Twitter className="h-4 w-4" />
          X
        </Button>
        
        {/* Telegram */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleShare('telegram')}
          className="gap-2 hover:bg-sky-50 hover:text-sky-600 hover:border-sky-200"
          data-testid="share-telegram"
        >
          <TelegramIcon className="h-4 w-4" />
          Telegram
        </Button>
        
        {/* LinkedIn */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleShare('linkedin')}
          className="gap-2 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
          data-testid="share-linkedin"
        >
          <Linkedin className="h-4 w-4" />
          LinkedIn
        </Button>
        
        {/* Copy Link */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopyLink}
          className={`gap-2 transition-colors ${copied ? 'bg-green-50 text-green-600 border-green-200' : 'hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200'}`}
          data-testid="share-copy"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? t('share.copied') : t('share.copyLink')}
        </Button>
      </div>
    </div>
  );
}
